import { t } from '@/i18n';

interface SlotCounterProps {
  spotsRemaining: number | null;
  capacity: number | null;
}

export default function SlotCounter({ spotsRemaining, capacity }: SlotCounterProps) {
  if (capacity === null) {
    return null;
  }

  const isFull = spotsRemaining !== null && spotsRemaining <= 0;
  const isLow = spotsRemaining !== null && spotsRemaining > 0 && spotsRemaining <= 5;
  const filled = spotsRemaining !== null ? capacity - spotsRemaining : null;
  const fillPercent = filled !== null ? Math.min(100, (filled / capacity) * 100) : 0;

  return (
    <div className="rounded-2xl border border-gray-200 border-t-2 border-t-teal-500 bg-white p-6 shadow-sm dark:border-gray-800 dark:border-t-teal-500 dark:bg-gray-950">
      <h3 className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
        {t('Availability')}
      </h3>
      <div className="mt-3">
        {isFull ? (
          <div>
            <p className="text-2xl font-bold text-red-600 dark:text-red-400">
              {capacity}
              <span className="ml-1 text-base font-normal text-gray-500 dark:text-gray-400">
                / {capacity}
              </span>
            </p>
            <p className="mt-1 text-sm text-red-600 dark:text-red-400">
              {t('No spots remaining')}
            </p>
          </div>
        ) : (
          <div>
            <p className={`text-2xl font-bold ${isLow ? 'text-amber-600 dark:text-amber-400' : 'text-gray-900 dark:text-white'}`}>
              {filled !== null ? filled : '—'}
              <span className="ml-1 text-base font-normal text-gray-500 dark:text-gray-400">
                / {capacity}
              </span>
            </p>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {isLow ? t('Almost full — register soon!') : t('registered')}
            </p>
          </div>
        )}

        {/* Progress bar — fills as spots are taken */}
        {filled !== null && (
          <div className="mt-4">
            <div className="h-2 w-full overflow-hidden rounded-full bg-gray-100 dark:bg-gray-800">
              <div
                className={`h-full rounded-full transition-all duration-500 ${
                  isFull
                    ? 'bg-red-500'
                    : isLow
                      ? 'bg-amber-500'
                      : 'bg-gradient-to-r from-teal-500 to-cyan-400'
                }`}
                style={{ width: `${fillPercent}%` }}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
